import { UserRequester } from '@cobliteam/api-lib';
import type { FleetUser } from '@cobliteam/api-lib/src/__generated__/graphql';
import FuscaFleetRequester, { GraphQLFleetRequester } from '@cobliteam/api-lib/src/dashboard/fleet';

import type { FuscaFleetAddress, FuscaFleetAddressType, FuscaFleetInfo, User, UUID } from '@cobliteam/common-types';
import { Environment } from '@cobliteam/react-environments';
import type { AppState } from '@cobliteam/store';
import Alert from '@cobliteam/ui/components/Alert';
import { type UseQueryOptions, useMutation, useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import invariant from 'tiny-invariant';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues
const getUserRequester = () => new UserRequester({ host: env.herbieApiHost });
const getFuscaFleetRequester = () => new FuscaFleetRequester({ host: env.apiHost });
const getGraphqlFleetRequester = () => new GraphQLFleetRequester();

export const QUERY_KEY = ['FLEET'];
export const QUERY_KEY_FLEET_USERS = [...QUERY_KEY, 'USERS'];

const useFleet = () => {
  const session = useSelector((state: AppState) => state.login.session);
  const currentFleetId = session?.fleet.id;

  const getFleetId = (): UUID => {
    invariant(currentFleetId, 'Fleet id is not defined');
    return currentFleetId;
  };

  const getFleetName = (): string | undefined => {
    return session?.fleet.name;
  };

  // @deprecated use useGetFleetUser instead
  const getFleetUsers = (options?: Partial<UseQueryOptions<User[]>>) => {
    return useQuery<User[]>({
      queryKey: [...QUERY_KEY_FLEET_USERS, 'DEPRECATED'],
      queryFn: () => getUserRequester().getFleetUsers(getFleetId()),
      staleTime: Number.POSITIVE_INFINITY,
      ...options,
    });
  };

  const useGetFleetUsers = () => {
    return useQuery<FleetUser[]>({
      queryKey: [...QUERY_KEY_FLEET_USERS],
      queryFn: () => getGraphqlFleetRequester().getFleetUsers(getFleetId()),
    });
  };

  const useDeleteUser = () => {
    return useMutation<void, unknown, string>({
      mutationFn: async (token: string) => getUserRequester().deleteFleetUser(token),
      onSuccess: () => {
        Alert.success('Usuário removido com sucesso!');
      },
      onError: () => {
        Alert.error('Houve um erro, tente novamente mais tarde.');
      },
    });
  };

  return {
    getFleetId,
    getFleetName,
    getFleetUsers,
    useDeleteUser,
    useGetFleetUsers,
  };
};

const useGetFleetInfo = (fleetId: UUID, addressType?: FuscaFleetAddressType) => {
  return useQuery<FuscaFleetInfo>({
    queryKey: [QUERY_KEY, 'INFO', fleetId, addressType],
    queryFn: () => getFuscaFleetRequester().getFleetInfo(fleetId, addressType),
  });
};

const useCreateFleetAddress = (fleetId: UUID) => {
  return useMutation({
    mutationFn: (info: FuscaFleetAddress) => getFuscaFleetRequester().createFleetAddress(fleetId, info),
  });
};

const useUpdateFleetAddress = (fleetId: UUID) => {
  return useMutation({
    mutationFn: (info: FuscaFleetAddress) => getFuscaFleetRequester().updateFleetAddress(fleetId, info),
  });
};

// FIXME: This is a temporary solution to get the user by id - api not implemented yet
const useGetUserById = ({ userId }: { userId: UUID }) => {
  const { useGetFleetUsers } = useFleet();

  const { data: fleetUsers, ...rest } = useGetFleetUsers();

  const user = fleetUsers?.find((user) => user.id === userId);

  return { data: user, ...rest };
};

export default useFleet;
export { useCreateFleetAddress, useGetFleetInfo, useGetUserById, useUpdateFleetAddress };
